import React from "react";
import * as utf8 from "utf8";
import type { Environment } from "../../../client/stores";
import { PayloadInterface, PayloadInterfaceLegacy } from "../decoders/Content.types";
import type { Site } from "../decoders/Sites.types";
import type { ContentPayload } from "../generated/webapi.g";
import { isPayloadInterface } from "./guards";
import type { QueryProps } from "./request";

// Global regex that are in a constant scope are stateful
// See the wtf: https://stackoverflow.com/questions/3811890/javascript-regular-expression-fails-every-other-time-it-is-called
const RE_SLUG_SPACE = () => /[-\s]+/g;
export const NAVBAR_HEIGHT = 68;

export const sanitize = (value: string) => {
    return value.replace(/[^\p{L}\p{P}\d ]/gu, "");
};

export const slugify = (value = "", max = 0) => {
    const RE_SLUG_UNWANTED = /[^\w\s-]+/g;
    const slug = value.normalize("NFKD").toLowerCase().replace(RE_SLUG_UNWANTED, "").replace(RE_SLUG_SPACE(), "-");
    return limit_by_words(slug, max, "-");
};

export const limit_by_words = (value: string, max: number, delimiter = "-") => {
    if (max < 1) {
        return value;
    }

    return value.split(delimiter).slice(0, max).join(delimiter);
};

export const stripSpace = (value: string) => {
    return value.replace(RE_SLUG_SPACE(), "");
};

export const stripTags = (value: string) => {
    const RE_HTML_TAGS = /(<!--.*?-->|<[^>]*>)/g;
    return value.replace(RE_HTML_TAGS, "");
};

export const removePrefix =
    (pattern: RegExp, replaceWith = "") =>
    (item: string) =>
        item.replace(pattern, replaceWith).trim();

export const matchPrefix = (pattern: RegExp) => (item: string | null) => {
    if (item) {
        const test = item.match(pattern);
        return !!(test && test.length);
    }

    return false;
};

export const spaceToUnderscore = (value: string) => value.replace(" ", "_").toLowerCase();

export const isProtocolAgnostic = (url: string) => url.indexOf("//") === 0;

export const trimText = (description: string, length: number, showOmission = true) => {
    if (!description) {
        return "";
    }
    if (description.length > length) {
        const omission = showOmission ? "..." : "";
        return description.trim().substring(0, length).split(" ").slice(0, -1).join(" ") + omission;
    }
    return description;
};

// If we have a scenario where we have two uses of this, like nav plus site leave modal (from PRN),
// then this will preserve the body-block.
const usages: { [key: string]: boolean } = {};
export const bodyScroll = (allow: boolean, key: string) => {
    if (typeof document !== "undefined") {
        const body = document.querySelector("body");
        if (allow) {
            usages[key] = false;
            if (!Object.values(usages).some(Boolean)) {
                (body as HTMLElement).classList.remove("no-scroll");
            }
        } else {
            usages[key] = true;
            (body as HTMLElement).classList.add("no-scroll");
        }
    }
};

export const getSocialShareInsertion = (payload: PayloadInterfaceLegacy | ContentPayload | PayloadInterface) => {
    const insertionBlockType = ["embed", "asset"];

    let insertSocialButtons: boolean;

    if (isPayloadInterface(payload)) {
        const { blocks } = payload.fields;
        insertSocialButtons = !!blocks.value?.length && insertionBlockType.includes(blocks.value[0].type);
    } else {
        insertSocialButtons = payload.blocks.length > 1 && insertionBlockType.includes(payload.blocks[0].type);
    }

    return { insertSocialButtons, insertSocialButtonsPosition: 0 };
};

export function getLiveStationUrl(site: Site, env: Environment) {
    const slug = site.sections.general?.name ? slugify(site.sections.general.name) : "";
    const id = site.getStreamId();
    const sc = "inferno";
    const campid = "a";
    const pname = site.index.slug.toUpperCase();

    if (id) {
        return `${env.NATIONAL_SITE_URL}/live/${slug}-${id}/?sc=${sc}&campid=${campid}&pname=${pname}`;
    }
    return null;
}

export const base64Decode = (value: string) => {
    if (value) {
        return typeof atob !== "undefined"
            ? atob(utf8.encode(value))
            : Buffer.from(utf8.encode(value)).toString("ascii");
    }

    return value;
};

export const objectToGetParams = (object: QueryProps) => {
    if (object) {
        const params = Object.entries(object)
            .filter(([, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

        return params.length > 0 ? `?${params.join("&")}` : "";
    }

    return "";
};

export const promiseTimeout = <T>(ms: number, promise: Promise<T>) => {
    // Create a promise that rejects in <ms> milliseconds
    const timeout = new Promise((resolve, reject) => {
        const id = setTimeout(() => {
            clearTimeout(id);
            reject(`Timed out in ${ms} ms.`);
        }, ms);
    });

    // Returns a race between our timeout and the passed in promise
    return Promise.race([promise, timeout]);
};

export const orderObjectByKeys = (object: { [key: string]: any }) => {
    return Object.keys(object)
        .sort()
        .reduce((obj, key) => {
            obj[key] = object[key];
            return obj;
        }, {});
};

export const scrollToAnchor = (element?: HTMLElement | null) => {
    if (element) {
        const top = element.offsetTop;
        const left = element.offsetLeft;
        window.scrollTo(left, top - NAVBAR_HEIGHT - 20);
    }
};

export const bindHashAnchorLinks = (contentRef: React.RefObject<HTMLDivElement>) => {
    contentRef.current?.querySelectorAll("a[href^='#']").forEach(element => {
        (element as HTMLAnchorElement).onclick = event => {
            const id = element.getAttribute("href")?.split("#")[1];
            if (id) {
                scrollToAnchor(document.getElementById(id));
            }
            event.preventDefault();
            return false;
        };
    });
};

export const isWindowDefined = () => typeof window !== "undefined";

export const checkValue = (value: any, defaultValue: any) => value || defaultValue;
